import React, { useState, useMemo } from "react";
import {
  Modal,
  Typography,
  Tabs,
  Button,
  Form,
  Input,
  Empty,
  Carousel,
  Spin,
} from "antd";
import { File, AlertCircle, Package, Eye, Box, MapPin } from "lucide-react";
import { TInventory } from "~/types";
import { useNotification } from "~/hooks";
import {
  useGetServiceTicket,
} from "~/services/service-ticket";
import { useQueryClient } from "@tanstack/react-query";
import { useGetFiles } from "~/services/file/file.queries";
import { NOTE_KEYS, useCreateNote, useGetNotes } from "~/services/note";
import { SortOrder, InventoryStatusCode, InventoryType, AddressTypes } from "~/utils/constants";
import { DocumentList } from "~/components/staff/documents/document-list";
import { capitalize, formatDimensions, formatFullAddress, formatPackingType, getClientName } from "~/utils";
import { SortByField } from "~/services/inventory";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ServiceDetailsModalProps {
  visible: boolean;
  ticketId: string | null;
  onClose: () => void;
}

const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  visible,
  ticketId,
  onClose,
}) => {
  const { data: ticket, isLoading } = useGetServiceTicket(ticketId);
  const [activeTab, setActiveTab] = useState("inventory");
  const [selectedInventory, setSelectedInventory] = useState<TInventory | null>(null);
  const [sortBy, setSortBy] = useState<SortByField | string>(SortByField.INVENTORY_REF);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);


  // Reset state when ticket changes
  React.useEffect(() => {
    if (ticket) {
      if (ticket.inventories && ticket.inventories.length > 0) {
        setSelectedInventory(ticket.inventories[0]);
      }
    }
  }, [ticket]);

  const handleSort = (field: SortByField | string) => {
    if (sortBy === field) {
      setSortOrder(
        sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
      );
    } else {
      setSortBy(field);
      setSortOrder(SortOrder.ASC);
    }
  };

  const sortedInventories = useMemo(() => {
    if (!ticket?.inventories) return [];

    return [...ticket.inventories].sort((a, b) => {
      const aValue = (a as any)[sortBy] || "";
      const bValue = (b as any)[sortBy] || "";
      const compare = String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: "base",
      });
      return sortOrder === SortOrder.ASC ? compare : -compare;
    });
  }, [ticket?.inventories, sortBy, sortOrder]);

  const getInventoryIcon = (itemType?: InventoryType) => {
    switch (itemType) {
      case InventoryType.PACKAGE:
        return <Box size={18} className="text-amber-500" />;
      default:
        return <Package size={18} className="text-green-500" />;
    }
  };

  const getStatusColor = (status?: InventoryStatusCode) => {
    switch (status) {
      case InventoryStatusCode.AVAILABLE_LIVE:
        return "bg-green-100 text-green-800";
      case InventoryStatusCode.AVAILABLE_ON_HAND:
        return "bg-blue-100 text-blue-800";
      case InventoryStatusCode.NO_POSSESSION:
        return "bg-orange-100 text-orange-800";
      case InventoryStatusCode.RELEASED:
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!ticket) return null;

  const jobTicketId = ticket?.jobTicket?.id;

  return (
    <Modal
      title={
        <div className="border-b pb-4">
          <Title level={4} className="mb-1">
            Service Details
          </Title>
          <div className="flex items-center gap-4 text-sm">
            <Text className="text-gray-600">
              <span className="font-semibold">Ticket:</span> {ticket.serial}
            </Text>
            <Text className="text-gray-600">
              <span className="font-semibold">Type:</span> {capitalize(ticket.serviceType)}
            </Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ maxWidth: "1400px" }}
      footer={null}
      loading={isLoading}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} className="mt-4">
        {/* Inventory Tab */}
        <Tabs.TabPane
          tab={
            <span className="flex items-center gap-2">
              <Package size={16} />
              Inventory
            </span>
          }
          key="inventory"
        >
          <div className="space-y-6">
            {/* Inventory Table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 text-left text-xs text-gray-500">
                  <tr>
                    <th
                      className="px-4 py-3 cursor-pointer hover:text-blue-600"
                      onClick={() => handleSort(SortByField.INVENTORY_REF)}
                    >
                      <div className="flex items-center">
                        <span>ID</span>
                        <span className="ml-2 text-xs">
                          <span className={sortBy === SortByField.INVENTORY_REF && sortOrder === SortOrder.ASC ? "text-blue-600" : "text-gray-400"}>↑</span>
                          <span className={sortBy === SortByField.INVENTORY_REF && sortOrder === SortOrder.DESC ? "text-blue-600" : "text-gray-400"}>↓</span>
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 cursor-pointer hover:text-blue-600"
                      onClick={() => handleSort("clientInternalRef")}
                    >
                      <div className="flex items-center">
                        <span>Client Ref #</span>
                        <span className="ml-2 text-xs">
                          <span className={sortBy === "clientInternalRef" && sortOrder === SortOrder.ASC ? "text-blue-600" : "text-gray-400"}>↑</span>
                          <span className={sortBy === "clientInternalRef" && sortOrder === SortOrder.DESC ? "text-blue-600" : "text-gray-400"}>↓</span>
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 cursor-pointer hover:text-blue-600"
                      onClick={() => handleSort(SortByField.TITLE)}
                    >
                      <div className="flex items-center">
                        <span>Title</span>
                        <span className="ml-2 text-xs">
                          <span className={sortBy === SortByField.TITLE && sortOrder === SortOrder.ASC ? "text-blue-600" : "text-gray-400"}>↑</span>
                          <span className={sortBy === SortByField.TITLE && sortOrder === SortOrder.DESC ? "text-blue-600" : "text-gray-400"}>↓</span>
                        </span>
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 cursor-pointer hover:text-blue-600"
                      onClick={() => handleSort(SortByField.ARTIST_NAME)}
                    >
                      <div className="flex items-center">
                        <span>Artist</span>
                        <span className="ml-2 text-xs">
                          <span className={sortBy === SortByField.ARTIST_NAME && sortOrder === SortOrder.ASC ? "text-blue-600" : "text-gray-400"}>↑</span>
                          <span className={sortBy === SortByField.ARTIST_NAME && sortOrder === SortOrder.DESC ? "text-blue-600" : "text-gray-400"}>↓</span>
                        </span>
                      </div>
                    </th>
                    <th className="px-4 py-3">Medium</th>
                    <th className="px-4 py-3">Packing</th>
                    <th className="px-4 py-3">Dimensions</th>
                    <th className="px-4 py-3">Value</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Location</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedInventories.map((item) => (
                    <tr
                      key={item.id}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        selectedInventory?.id === item.id ? "bg-blue-50" : ""
                      }`}
                      onClick={() => setSelectedInventory(item)}
                    >
                      <td className="px-4 py-3 text-sm">{item.inventoryRef}</td>
                      <td className="px-4 py-3 font-medium">{item.clientInternalRef}</td>
                      <td className="px-4 py-3 font-medium">{item.title}</td>
                      <td className="px-4 py-3 text-sm">{item.artistName}</td>
                      <td className="px-4 py-3 text-sm">{item.medium}</td>
                      <td className="px-4 py-3 text-sm">{formatPackingType(item)}</td>
                      <td className="px-4 py-3 text-sm">{formatDimensions(item)}</td>
                      <td className="px-4 py-3 text-sm">
                        ${item.value?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                            item.status
                          )}`}
                        >
                          {capitalize(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{item.location?.name}</td>
                      <td className="px-4 py-3">
                        <button
                          className="p-1 text-gray-400 hover:text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedInventory(item);
                          }}
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sortedInventories.length === 0 && (
                    <tr>
                      <td colSpan={11} className="px-4 py-6 text-center text-gray-500">
                        No inventory items associated with this service
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Inventory Details Panel */}
            {selectedInventory && (
              <div className="bg-gray-50 border rounded-lg p-6">
                <h3 className="text-lg font-medium mb-4">Inventory Details</h3>
                <div className="grid grid-cols-3 gap-6">
                  {/* Image Carousel */}
                  <div className="col-span-1">
                    <div className="bg-white border rounded-lg p-4 h-64">
                      {selectedInventory.images && selectedInventory.images.length > 0 ? (
                        <Carousel autoplay autoplaySpeed={3000} arrows className="h-56">
                          {selectedInventory.images.map((image, index) => (
                            <div key={image.id || index} className="h-56 flex items-center justify-center">
                              <img
                                src={image.url}
                                alt={`${selectedInventory.title} - Image ${index + 1}`}
                                className="max-w-full max-h-56 object-contain"
                              />
                            </div>
                          ))}
                        </Carousel>
                      ) : (
                        <div className="h-full flex items-center justify-center text-center text-gray-400">
                          <div>
                            <Package size={48} className="mx-auto mb-2" />
                            <p>No preview available</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Item Details */}
                  <div className="col-span-2">
                    <div className="bg-white border rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Text className="text-xs text-gray-500">Inventory ID</Text>
                          <p className="font-medium">{selectedInventory.inventoryRef}</p>
                        </div>
                        <div>
                          <Text className="text-xs text-gray-500">Client Reference</Text>
                          <p className="font-medium">{selectedInventory.clientInternalRef || "N/A"}</p>
                        </div>
                        <div className="col-span-2">
                          <Text className="text-xs text-gray-500">Title</Text>
                          <p className="font-medium">{selectedInventory.title}</p>
                        </div>
                        <div>
                          <Text className="text-xs text-gray-500">Artist</Text>
                          <p className="font-medium">{selectedInventory.artistName || "N/A"}</p>
                        </div>
                        <div>
                          <Text className="text-xs text-gray-500">Medium</Text>
                          <p className="font-medium">{selectedInventory.medium || "N/A"}</p>
                        </div>
                        <div>
                          <Text className="text-xs text-gray-500">Dimensions</Text>
                          <p className="font-medium">{formatDimensions(selectedInventory)}</p>
                        </div>
                        <div>
                          <Text className="text-xs text-gray-500">Value</Text>
                          <p className="font-medium">${selectedInventory.value?.toLocaleString()}</p>
                        </div>
                        <div>
                          <Text className="text-xs text-gray-500">Location</Text>
                          <p className="font-medium">{selectedInventory.location?.name || "N/A"}</p>
                        </div>
                        <div>
                          <Text className="text-xs text-gray-500">Status</Text>
                          <p>
                            <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedInventory.status)}`}>
                              {capitalize(selectedInventory.status)}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Tabs.TabPane>

        {/* Documents Tab */}
        <Tabs.TabPane
          tab={
            <span className="flex items-center gap-2">
              <File size={16} />
              Documents
            </span>
          }
          key="documents"
        >
          <DocumentTab jobTicketId={jobTicketId} />
        </Tabs.TabPane>

        {/* Staff Notes Tab */}
        <Tabs.TabPane
          tab={
            <span className="flex items-center gap-2">
              <AlertCircle size={16} />
              Staff Notes
            </span>
          }
          key="notes"
        >
          <StaffNotesTab jobTicketId={jobTicketId} serviceTicketId={ticket.id} legacyStaffNotes={ticket.staffNotes} />
        </Tabs.TabPane>

        {/* Location Notes Tab */}
        <Tabs.TabPane
          tab={
            <span className="flex items-center gap-2">
              <MapPin size={16} />
              Location Notes
            </span>
          }
          key="location"
        >
          <div className="bg-white border rounded-lg p-6">
            <div className="space-y-6">
              {/* Location Address */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium">Service Location</h3>
                  <span className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                    {ticket.location?.type ? capitalize(ticket.location.type.replace(/_/g, ' ')) : 'Not specified'}
                  </span>
                </div>
                <div className="bg-gray-50 border rounded-lg p-4">
                  <div className="space-y-2">
                    {ticket.location?.addressName && (
                      <div>
                        <Text className="text-xs text-gray-500">Location Name</Text>
                        <p className="font-medium text-base">{ticket.location.addressName}</p>
                      </div>
                    )}
                    <div>
                      <Text className="text-xs text-gray-500">Address</Text>
                      <p className="font-medium text-base whitespace-pre-line">
                        {formatFullAddress(ticket.location)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Notes */}
              {ticket.location?.specialInstructions && (
                <div>
                  <h3 className="text-lg font-medium mb-3">Address Notes</h3>
                  <div className="bg-gray-50 border rounded-lg p-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {ticket.location.specialInstructions}
                    </p>
                  </div>
                </div>
              )}

              {!ticket.location?.specialInstructions && (
                <div>
                  <h3 className="text-lg font-medium mb-3">Address Notes</h3>
                  <div className="bg-gray-50 border rounded-lg p-4 text-center text-gray-500">
                    No address notes for this location
                  </div>
                </div>
              )}
            </div>
          </div>
        </Tabs.TabPane>
      </Tabs>
    </Modal>
  );
};

const DocumentTab = ({ jobTicketId }: { jobTicketId?: string }) => {
  const { data: filesData, isLoading: isLoadingFiles } = useGetFiles(
    {
      jobTicketId,
      sortByCreatedAt: SortOrder.DESC,
    },
    !!jobTicketId
  );
  const documents = filesData?.items || [];

  return (
    <div className="bg-white border rounded-lg p-6">
      <DocumentList
        documents={documents}
        isLoading={isLoadingFiles}
        onView={(doc) => {
          if (doc.filePath) {
            window.open(doc.filePath, "_blank");
          }
        }}
      />
    </div>
  );
};

const StaffNotesTab = ({
  jobTicketId,
  serviceTicketId,
  legacyStaffNotes,
}: {
  jobTicketId?: string;
  serviceTicketId?: string;
  legacyStaffNotes?: string;
}) => {
  const [noteContent, setNoteContent] = useState("");
  const { success, error } = useNotification();
  const qc = useQueryClient();
  const createNoteMutation = useCreateNote();

  const { data: notesData, isLoading } = useGetNotes(
    { serviceTicketId, sortByCreatedAt: SortOrder.DESC },
  );

  const notes = notesData?.items || [];

  const handleAddNote = () => {
    if (!noteContent.trim() || !serviceTicketId) return;

    createNoteMutation.mutate(
      { content: noteContent, jobTicketId, serviceTicketId },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: NOTE_KEYS.GET_NOTES({}) });
          success({ message: "Note added successfully" });
          setNoteContent("");
        },
        onError: () => {
          error({ message: "Failed to add note" });
        },
      }
    );
  };

  return (
    <div className="bg-white border rounded-lg p-6">
      <div className="mb-4">
        <TextArea
          rows={3}
          value={noteContent}
          onChange={(e) => setNoteContent(e.target.value)}
          placeholder="Add a note about this service..."
          className="text-base"
        />
        <div className="flex justify-end mt-2">
          <Button
            type="primary"
            onClick={handleAddNote}
            loading={createNoteMutation.isPending}
            disabled={!noteContent.trim()}
          >
            Add Note
          </Button>
        </div>
      </div>

      {legacyStaffNotes && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex justify-between items-start mb-1">
            <div className="font-medium text-sm text-amber-800">Admin Note</div>
          </div>
          <div className="text-sm whitespace-pre-wrap text-amber-900">
            {legacyStaffNotes}
          </div>
        </div>
      )}

      <Spin spinning={isLoading}>
        <div className="divide-y border rounded-lg overflow-hidden">
          {notes.length === 0 && !legacyStaffNotes ? (
            <div className="p-6 text-center text-gray-400">
              No notes yet. Add the first note above.
            </div>
          ) : notes.length === 0 ? null : (
            notes.map((note) => (
              <div key={note.id} className="p-4 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="font-medium text-sm">
                    {getClientName(note.author)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(note.createdAt).toLocaleString(undefined, {
                      month: "numeric",
                      day: "numeric",
                      year: "2-digit",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <div className="mt-1 text-sm whitespace-pre-wrap">
                  {note.content}
                </div>
              </div>
            ))
          )}
        </div>
      </Spin>
    </div>
  );
};

export default ServiceDetailsModal;